import { PrestationParticipationStatus, PrestationPricingMode, PrestationType, } from '@cpce/database/enums';
import { Decimal } from 'decimal.js';
import { isClientAccountLine } from './accounting.utils.js';
import { sumDecimals } from './decimal.utils.js';
export function getPrestationKind(prestation) {
    if (prestation.supplierId !== null)
        return 'sale';
    return prestation.isAdhesion ? 'adhesion' : 'creance';
}
export const PRESTATION_TYPE_RULES = {
    remboursement: {
        label: 'Remboursement',
        hasSupplier: false,
        pricingModes: [PrestationPricingMode.typedAmount, PrestationPricingMode.predefinedPrices],
    },
    billetterie: {
        label: 'Billetterie',
        hasSupplier: true,
        pricingModes: [PrestationPricingMode.predefinedPrices],
    },
    voyage: {
        label: 'Voyages',
        hasSupplier: true,
        pricingModes: [PrestationPricingMode.typedAmount, PrestationPricingMode.predefinedPrices],
    },
    cheques: {
        label: 'Chèques',
        hasSupplier: true,
        pricingModes: [PrestationPricingMode.predefinedPrices],
    },
    adhesion: {
        label: 'Adhésion',
        hasSupplier: false,
        pricingModes: [PrestationPricingMode.predefinedPrices],
    },
};
/** The family a prestation filed without one falls in; the type migration backfilled the same. */
export function inferPrestationType(prestation) {
    if (prestation.isAdhesion)
        return PrestationType.adhesion;
    if (prestation.supplierId === null)
        return PrestationType.remboursement;
    return prestation.pricingMode === PrestationPricingMode.typedAmount
        ? PrestationType.voyage
        : PrestationType.billetterie;
}
export function isPrestationTypeConsistent(prestation) {
    const rule = PRESTATION_TYPE_RULES[prestation.type];
    return (rule.hasSupplier === (prestation.supplierId !== null) &&
        (prestation.type === PrestationType.adhesion) === prestation.isAdhesion &&
        rule.pricingModes.includes(prestation.pricingMode));
}
/**
 * Whether the billetterie subsidises the prestation's dossiers: a sale always (even one left without
 * a target, which then estimates nothing), an adhésion when it has a target, a créance only on an
 * amount typed at registration (a remboursement owes the salarié the subsidy on their invoice, a
 * tarif créance owes the whole tarif).
 */
export function isSubsidisedByBilletterie(prestation) {
    switch (getPrestationKind(prestation)) {
        case 'sale':
            return true;
        case 'adhesion':
            return !!prestation.ticketingCategoryId || !!prestation.ticketingProductVariantCode;
        case 'creance':
            return prestation.pricingMode === PrestationPricingMode.typedAmount;
    }
}
const PRODUCT_ACCOUNT_RULE = {
    accountColumn: 'productFinancialAccountId',
    accountRelation: 'productFinancialAccount',
    accountClass: '7',
    accountLabel: 'de produit',
};
export const PRESTATION_KIND_RULES = {
    sale: PRODUCT_ACCOUNT_RULE,
    adhesion: PRODUCT_ACCOUNT_RULE,
    creance: {
        accountColumn: 'chargeFinancialAccountId',
        accountRelation: 'chargeFinancialAccount',
        accountClass: '6',
        accountLabel: 'de charge',
    },
};
/** A participant's line: the amount typed at registration, or their tarif times their places. */
export function computeParticipantAmount(participant) {
    if (participant.typedAmount !== null)
        return new Decimal(participant.typedAmount);
    if (!participant.prestationPrice) {
        throw new Error('A participant has neither a tarif nor a typed amount');
    }
    return new Decimal(participant.prestationPrice.price).mul(participant.quantity);
}
/**
 * A dossier's face value: its participants' lines summed. Tarifs and quantities can both differ
 * from one participant to the next.
 */
export function computeParticipationTotal(participation) {
    return sumDecimals(participation.participants.map(computeParticipantAmount));
}
// Employee amount = the signed movement on the salarié's 411 in the participation's écriture.
// A sale debits it (debit 411 / credit 7088, the salarié owes his share); a supplier-less
// prestation credits it (debit 6588 / credit 411, the CSE owes the salarié), which reads as a
// negative amount — a créance. Without an écriture, nobody owes anything (full commitee subsidy).
export function computePrestationEmployeeAmount(participation) {
    if (!participation.saleTransaction)
        return new Decimal(0);
    const movements = participation.saleTransaction.transactionAccountMovements;
    const clientMovement = movements.find(isClientAccountLine) ?? movements.find((m) => m.transactionType === 'debit');
    if (!clientMovement)
        return new Decimal(0);
    const amount = new Decimal(clientMovement.amount);
    return clientMovement.transactionType === 'debit' ? amount : amount.neg();
}
// A créance's écriture is exactly what the CSE owes: on a remboursement, the subsidy on an
// invoice whose rest the salarié keeps paying on their own.
export function computePrestationCommiteeAmount(participation) {
    const employeeAmount = computePrestationEmployeeAmount(participation);
    if (getPrestationKind(participation.prestation) === 'creance') {
        return new Decimal(0).minus(employeeAmount);
    }
    return computeParticipationTotal(participation).minus(Decimal.max(employeeAmount, 0));
}
// A cancelled dossier owes nothing: its sale écriture is reversed by a contre-passation écriture,
// so the net on the client account (411) is zero. We short-circuit here rather than netting the
// reversal movements so the debt matches the accounting regardless of how the reversal is queried.
export function calculatePrestationDebt(participation) {
    if (participation.cancelledAt)
        return new Decimal(0);
    return computePrestationEmployeeAmount(participation).minus(computePrestationPaidAmount(participation));
}
/**
 * A dossier with nothing left to pay is only validated once the billetterie has recorded its
 * subsidy: until then it waits, and the subsidy is asked for again whenever the dossier is synced.
 */
export function isAwaitingSubsidy(participation) {
    return (!participation.cancelledAt &&
        participation.status === PrestationParticipationStatus.awaitingValidation &&
        isSubsidisedByBilletterie(participation.prestation) &&
        calculatePrestationDebt(participation).lte(0));
}
// Cancelling a règlement reverses its écriture but keeps its allocation lines, so every read
// has to skip the ones whose règlement is cancelled. A rejet without report keeps them too, and
// takes back only its share.
export function computePrestationPaidAmount(participation) {
    return sumDecimals(participation.prestationTransactions
        .filter((allocation) => !allocation.transaction.cancelledAt)
        .map(computeAllocationPaidAmount));
}
export function computeAllocationPaidAmount(allocation) {
    return new Decimal(allocation.amount).minus(allocation.rejectedAmount);
}
export function hasActivePayment(participation) {
    return participation.prestationTransactions.some((allocation) => !allocation.transaction.cancelledAt);
}
// A cancelled dossier needs no receipt.
export function isJustificatifMissing(participation, justificatifCount) {
    return (participation.prestation.requiresJustificatif &&
        !participation.cancelledAt &&
        justificatifCount === 0);
}
export function hasDebt(participation) {
    return calculatePrestationDebt(participation).gt(0);
}
